import {
  type SystemNotice,
  NOTICE_SEVERITY_STYLES,
  NOTICE_TYPE_LABELS,
  formatCountdown,
} from '@tankef/core';

import { cn } from '../../utils';

export interface SystemBannerProps {
  notice: SystemNotice;
  onDismiss?: (id: string) => void;
}

export function SystemBanner({ notice, onDismiss }: SystemBannerProps) {
  const styles = NOTICE_SEVERITY_STYLES[notice.severity];
  const timeUntilMaintenance = notice.getTimeUntilMaintenance();

  return (
    <div className={cn('border-b px-4 py-3', styles.bg, styles.border)} role="alert">
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        <div className="flex items-center gap-3">
          <span>{styles.icon}</span>
          <div>
            <span className={cn('font-medium', styles.text)}>
              {NOTICE_TYPE_LABELS[notice.type]}:
            </span>{' '}
            <span className={styles.text}>{notice.title}</span>
            {notice.message && <span className={cn('ml-2', styles.text)}>— {notice.message}</span>}
            {timeUntilMaintenance !== null && timeUntilMaintenance > 0 && (
              <span className={cn('ml-2 font-mono', styles.text)}>
                (en {formatCountdown(timeUntilMaintenance)})
              </span>
            )}
          </div>
          {notice.link && (
            <a href={notice.link} className={cn('underline hover:no-underline', styles.text)}>
              {notice.linkText || 'Más información'}
            </a>
          )}
        </div>
        {notice.dismissible && onDismiss && (
          <button
            type="button"
            className={cn(
              'rounded-md p-1 hover:bg-tkf-overlay-light focus:outline-none',
              styles.text
            )}
            onClick={() => onDismiss(notice.id)}
            aria-label="Cerrar"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export interface SystemBannerContainerProps {
  notices: SystemNotice[];
  onDismiss: (id: string) => void;
}

export function SystemBannerContainer({ notices, onDismiss }: SystemBannerContainerProps) {
  if (notices.length === 0) return null;

  return (
    <div className="sticky top-0 z-40">
      {notices.map(notice => (
        <SystemBanner key={notice.id} notice={notice} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
